/**
 * @jsxRuntime classic
 * @jsx jsx
 */

/**
 * @deprecated
 * This hook implements the v1 (stateful, React hook-based) permission bridge.
 *
 * New code should use the v2 stateless bridge methods instead:
 *   `platform/packages/forge/forge-ui/src/bridge/methods/permissions/index.tsx`
 *
 * The v2 methods (`egressGet`, `egressSet`, `egressDeleteDomain`,
 * `egressDeleteGroup`, `remoteGet`, `remoteSet`) are registered as
 * `__permission__*` bridge methods and are gated by
 * `fg('configurable-egress-mvp-frontend')`.
 *
 * This file is retained to support the legacy custom-UI iframe bridge path
 * (`usePermission` hook consumed via `useBridge`). Do not add new permission
 * logic here.
 */

import type { MutableRefObject } from 'react';
import { useRef, useState } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ContextId, ExtensionId } from '@atlassian/forge-ui-types/extension';

import { BridgeClientError, type BridgeConfig } from '../..';
import { useForgeUiAnalyticsEvent } from '../../../../analytics/useForgeUiAnalyticsEvent';
import { AdminConsentModal as AdminConsentModalWithQueue } from '../../../../bridge/methods/permissions/AdminConsentModalWithQueue';
import { createBridgeMethod } from '../../createBridgeApi';
import type { ApiRefType } from '../../useBridge';
import { useForgeAppQuery } from '../useForgeAppQuery';
import { AdminModal } from './AdminConsentModal';
import type {
	EgressDeleteDomainPayload,
	EgressDeleteGroupPayload,
	EgressGetPayload,
	EgressSetPayload,
	RemoteGetPayload,
	RemoteSetPayload,
} from './types';
import { usePermissionFetchClient } from './usePermissionFetchClient';

export interface usePermissionProps {
	accountType?: 'atlassian' | 'customer' | 'anonymous';
	appId: string;
	contextIds: ContextId[];
	extensionId: ExtensionId;
	installationId: string;
	renderThreeLOPrompt: (retry: () => Promise<void>) => Promise<void>;
}

export type ManifestRemoteMap = Record<
	string,
	| {
			description: string;
			name: string;
			supportedPatterns: string[];
	  }
	| undefined
>;

type DomainInfoEgress = {
	payload: EgressSetPayload;
	type: 'EGRESS';
};

type DomainInfoRemote = {
	manifestRemoteMap: ManifestRemoteMap;
	payload: RemoteSetPayload;
	type: 'REMOTE';
};

export type PermissionBridgeMethods = {
	__permission__egressDeleteDomain?: (payload: any) => void;
	__permission__egressDeleteGroup?: (payload: any) => void;
	__permission__egressGet?: (payload?: any) => void;
	__permission__egressSet?: (payload?: any) => void;
	__permission__remoteGet?: (payload: any) => void;
	__permission__remoteSet?: (payload: any) => void;
};

export type ModalState =
	| { state: 'closed' }
	| {
			appName: string;
			domainInfo: DomainInfoEgress | DomainInfoRemote;
			onCancel: () => void;
			onConfirm: () => void;
			privacyPolicyLink?: string | null;
			state: 'open';
			termsOfUseLink?: string | null;
	  };

export type PermissionMethod =
	| 'egressSet'
	| 'egressGet'
	| 'egressDeleteDomain'
	| 'egressDeleteGroup'
	| 'remoteGet'
	| 'remoteSet';

export const permissionMethods: PermissionMethod[] = [
	'egressSet',
	'egressGet',
	'egressDeleteDomain',
	'egressDeleteGroup',
	'remoteGet',
	'remoteSet',
];

export const PERMISSION_METHOD_PREFIX = '__permission__';

const RATE_LIMIT_MS = 5000;

export const getUseBridgePermissionFunctions = (apiRef: MutableRefObject<ApiRefType>) => ({
	__permission__egressSet: async ({ data }: { data: any }): Promise<void | undefined> => {
		return apiRef.current.__permission__egressSet?.(data);
	},
	__permission__egressGet: async ({ data }: { data: any }): Promise<void | undefined> => {
		return apiRef.current.__permission__egressGet?.(data);
	},
	__permission__egressDeleteDomain: async ({ data }: { data: any }): Promise<void | undefined> => {
		return apiRef.current.__permission__egressDeleteDomain?.(data);
	},
	__permission__egressDeleteGroup: async ({ data }: { data: any }): Promise<void | undefined> => {
		return apiRef.current.__permission__egressDeleteGroup?.(data);
	},
	__permission__remoteGet: async ({ data }: { data: any }): Promise<void | undefined> => {
		return apiRef.current.__permission__remoteGet?.(data);
	},
	__permission__remoteSet: async ({ data }: { data: any }): Promise<void | undefined> => {
		return apiRef.current.__permission__remoteSet?.(data);
	},
});

export const getCreateBridgeApiPermissionFunctions = (
	bridge: BridgeConfig | undefined,
): PermissionBridgeMethods =>
	Object.fromEntries(
		permissionMethods.map((method) => [
			`${PERMISSION_METHOD_PREFIX}${method}`,
			createBridgeMethod(bridge?.[`${PERMISSION_METHOD_PREFIX}${method}`]),
		]),
	);

export function usePermission({
	appId,
	installationId,
	contextIds,
	extensionId,
	renderThreeLOPrompt,
}: usePermissionProps): {
	egressDeleteDomain: (payload: EgressDeleteDomainPayload) => Promise<any>;
	egressDeleteGroup: (payload: EgressDeleteGroupPayload) => Promise<any>;
	egressGet: (payload: EgressGetPayload) => Promise<any>;
	egressSet: (payload: EgressSetPayload) => Promise<any>;
	getModalJsx: () => JSX.Element | null;
	remoteGet: (payload: RemoteGetPayload) => Promise<any>;
	remoteSet: (payload: RemoteSetPayload) => Promise<void | {}>;
} {
	const [modalState, setModalState] = useState<ModalState>({ state: 'closed' });
	const [isRateLimited, setIsRateLimited] = useState(false);
	const { trackPermissionAction } = useForgeUiAnalyticsEvent();

	// ── Queue state (used when forge-ui-permission-consent-modal-improvements is ON) ──
	const queueRef = useRef<Array<() => void>>([]);
	const isModalOpenRef = useRef(false);

	// ── WithQueue modal state (individual props for AdminConsentModalWithQueue) ──
	const [withQueueModalState, setWithQueueModalState] = useState<{
		appName: string;
		domainInfo: DomainInfoEgress | DomainInfoRemote;
		onCancel: () => void;
		onConfirm: () => Promise<void>;
		privacyPolicyLink?: string | null;
		termsOfUseLink?: string | null;
	} | null>(null);

	const waitForModalSlot = (): Promise<void> => {
		if (!isModalOpenRef.current) {
			isModalOpenRef.current = true;
			return Promise.resolve();
		}
		return new Promise<void>((resolve) => {
			queueRef.current.push(() => {
				isModalOpenRef.current = true;
				resolve();
			});
		});
	};

	const releaseModalSlot = (): void => {
		isModalOpenRef.current = false;
		setWithQueueModalState(null);
		const next = queueRef.current.shift();
		if (next) {
			next();
		}
	};

	const permissionFetchClient = usePermissionFetchClient({
		appId,
		installationId,
		contextIds,
		extensionId,
		renderThreeLOPrompt,
	});

	const queryForgeApp = useForgeAppQuery();

	const fetchAppData = async (id: string, contextIds: string[]) => {
		try {
			return await queryForgeApp(id, contextIds);
		} catch (e) {
			if (e instanceof Error) {
				const error = new BridgeClientError(e.message);
				error.stack = e.stack;
				throw error;
			}
			throw new BridgeClientError(typeof e === 'string' ? e : String(e));
		}
	};

	const canOpenModal = (): boolean => {
		return modalState.state === 'closed' && !isRateLimited;
	};

	const noteModalOpened = (): void => {
		setIsRateLimited(true);
		setTimeout(() => {
			setIsRateLimited(false);
		}, RATE_LIMIT_MS);
	};

	return {
		getModalJsx: (): JSX.Element | null => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return null;
			}
			if (withQueueModalState && fg('forge-ui-permission-consent-modal-improvements')) {
				return (
					<AdminConsentModalWithQueue
						appName={withQueueModalState.appName}
						domainInfo={withQueueModalState.domainInfo}
						onCancel={withQueueModalState.onCancel}
						onConfirm={withQueueModalState.onConfirm}
						termsOfUseLink={withQueueModalState.termsOfUseLink}
						privacyPolicyLink={withQueueModalState.privacyPolicyLink}
					/>
				);
			}
			return <AdminModal modalState={modalState} />;
		},
		egressSet: async (payload: EgressSetPayload): Promise<any> => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}
			trackPermissionAction({
				appId,
				installationId,
				action: 'egressSet',
			});

			// ── Queue path (forge-ui-permission-consent-modal-improvements) ──
			if (fg('forge-ui-permission-consent-modal-improvements')) {
				await waitForModalSlot();

				let app;
				try {
					app = await fetchAppData(appId, contextIds);
				} catch (err) {
					releaseModalSlot();
					throw err;
				}

				const dryRunResp = await permissionFetchClient.egressSet(payload, true);
				if (!dryRunResp.ok) {
					releaseModalSlot();
					throw new BridgeClientError('Dry run failed');
				}

				return new Promise<void>((resolve, reject) => {
					setWithQueueModalState({
						appName: app.name,
						domainInfo: { type: 'EGRESS', payload },
						termsOfUseLink: app.termsOfService,
						privacyPolicyLink: app.privacyPolicy,
						onCancel: () => {
							releaseModalSlot();
							reject(new BridgeClientError('Setting egress was rejected by admin'));
						},
						onConfirm: async () => {
							const setResp = await permissionFetchClient.egressSet(payload, false);
							const json = await setResp.json();
							if (!setResp.ok) {
								throw new BridgeClientError(JSON.stringify(json, null, 2));
							}
							releaseModalSlot();
							resolve(json);
						},
					});
				});
			}

			// ── Legacy rate-limit path ──
			const app = await fetchAppData(appId, contextIds);
			const response = await permissionFetchClient.egressSet(payload, true);

			if (!response.ok) {
				throw new BridgeClientError('Dry run failed');
			}

			if (!canOpenModal()) {
				throw new BridgeClientError(
					'Permission request rate limited. Please wait before making another request.',
				);
			}

			return new Promise<void>((resolve, reject) => {
				noteModalOpened();
				setModalState({
					state: 'open',
					onCancel: () => {
						setModalState({ state: 'closed' });
						reject('Setting egress was rejected by admin');
					},
					onConfirm: async () => {
						try {
							const setEgressResp = await permissionFetchClient.egressSet(payload, false);
							const json = await setEgressResp.json();

							if (!setEgressResp.ok) {
								throw new BridgeClientError(JSON.stringify(json, null, 2));
							}

							setModalState({ state: 'closed' });
							resolve(json);
						} catch (err) {
							reject(err);
						}
					},
					domainInfo: { type: 'EGRESS', payload },
					appName: app.name,
					termsOfUseLink: app.termsOfService,
					privacyPolicyLink: app.privacyPolicy,
				});
			});
		},
		egressGet: async (payload: EgressGetPayload): Promise<any> => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}
			trackPermissionAction({
				appId,
				installationId,
				action: 'egressGet',
			});

			try {
				const getEgressResp = await permissionFetchClient.egressGet(payload);
				const json = await getEgressResp.json();

				if (!getEgressResp.ok) {
					throw new BridgeClientError(JSON.stringify(json, null, 2));
				}

				return json;
			} catch (err) {
				throw new BridgeClientError(`Could not get egress due to: ${err}`);
			}
		},
		egressDeleteDomain: async (payload: EgressDeleteDomainPayload): Promise<any> => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}
			trackPermissionAction({
				appId,
				installationId,
				action: 'egressDeleteDomain',
			});

			try {
				const egressDeleteDomainResp = await permissionFetchClient.egressDeleteDomain(payload);
				if (egressDeleteDomainResp.ok && egressDeleteDomainResp.status === 204) {
					return undefined;
				}
				const json = await egressDeleteDomainResp.json();

				if (!egressDeleteDomainResp.ok) {
					throw new BridgeClientError(JSON.stringify(json, null, 2));
				}

				return json;
			} catch (err) {
				throw new BridgeClientError(`Could not delete egress domain due to ${err}`);
			}
		},
		egressDeleteGroup: async (payload: EgressDeleteGroupPayload): Promise<any> => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}
			trackPermissionAction({
				appId,
				installationId,
				action: 'egressDeleteGroup',
			});

			try {
				const egressDeleteGroupResp = await permissionFetchClient.egressDeleteGroup(payload);
				if (egressDeleteGroupResp.ok && egressDeleteGroupResp.status === 204) {
					return undefined;
				}
				const json = await egressDeleteGroupResp.json();

				if (!egressDeleteGroupResp.ok) {
					throw new BridgeClientError(JSON.stringify(json, null, 2));
				}

				return json;
			} catch (err) {
				throw new BridgeClientError(`Could not delete egress group due to ${err}`);
			}
		},
		remoteGet: async (payload: RemoteGetPayload): Promise<any> => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}
			trackPermissionAction({
				appId,
				installationId,
				action: 'remoteGet',
			});

			try {
				const remoteGetResp = await permissionFetchClient.remoteGet(payload);
				const json = await remoteGetResp.json();

				if (!remoteGetResp.ok) {
					throw new BridgeClientError(JSON.stringify(json, null, 2));
				}

				return json;
			} catch (err) {
				throw new BridgeClientError(`Could not get remote due to: ${err}`);
			}
		},
		remoteSet: async (payload: RemoteSetPayload): Promise<void | {}> => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}
			trackPermissionAction({
				appId,
				installationId,
				action: 'remoteSet',
			});

			// ── Queue path (forge-ui-permission-consent-modal-improvements) ──
			if (fg('forge-ui-permission-consent-modal-improvements')) {
				await waitForModalSlot();

				let app;
				try {
					app = await fetchAppData(appId, contextIds);
				} catch (err) {
					releaseModalSlot();
					throw err;
				}

				const dryRunResp = await permissionFetchClient.remoteSet(payload, true);
				if (!dryRunResp.ok) {
					releaseModalSlot();
					throw new BridgeClientError('Dry run failed');
				}

				const node = (app.installationsByContexts?.edges ?? [])[0]?.node;
				const payloadKeys = new Set(payload.remotes.map((r) => r.key));
				const manifestRemoteMap = (node?.appEnvironmentVersion?.extensions?.nodes ?? [])
					.filter(
						(e) =>
							e && e.extensionData.type === 'core:remote' && payloadKeys.has(e.extensionData.key),
					)
					.reduce((acc, e) => {
						if (e?.extensionData) {
							acc[e.extensionData.key] = e.extensionData.configurable;
						}
						return acc;
					}, {} as ManifestRemoteMap);

				return new Promise<void>((resolve, reject) => {
					setWithQueueModalState({
						appName: app.name,
						domainInfo: { type: 'REMOTE', payload, manifestRemoteMap },
						termsOfUseLink: app.termsOfService,
						privacyPolicyLink: app.privacyPolicy,
						onCancel: () => {
							releaseModalSlot();
							reject(new BridgeClientError('Setting remote was rejected by admin'));
						},
						onConfirm: async () => {
							const setResp = await permissionFetchClient.remoteSet(payload, false);
							if (!setResp.ok) {
								throw new BridgeClientError(setResp.statusText);
							}
							releaseModalSlot();
							resolve(undefined);
						},
					});
				});
			}

			// ── Legacy rate-limit path ──
			const app = await fetchAppData(appId, contextIds);

			const response = await permissionFetchClient.remoteSet(payload, true);

			if (!response.ok) {
				throw new BridgeClientError('Dry run failed');
			}

			if (!canOpenModal()) {
				throw new BridgeClientError(
					'Permission request rate limited. Please wait before making another request.',
				);
			}

			const node = (app.installationsByContexts?.edges ?? [])[0]?.node;
			const payloadKeys = new Set(payload.remotes.map((r) => r.key));
			const manifestRemoteMap = (node?.appEnvironmentVersion?.extensions?.nodes ?? [])
				.filter(
					(e) =>
						e && e.extensionData.type === 'core:remote' && payloadKeys.has(e.extensionData.key),
				)
				.reduce((acc, e) => {
					if (e?.extensionData) {
						acc[e.extensionData.key] = e.extensionData.configurable;
					}
					return acc;
				}, {} as ManifestRemoteMap);

			return new Promise<void>((resolve, reject) => {
				noteModalOpened();
				setModalState({
					state: 'open',
					onCancel: () => {
						setModalState({ state: 'closed' });
						reject(new BridgeClientError('Setting remote was rejected by admin'));
					},
					onConfirm: async () => {
						try {
							const remoteSetResp = await permissionFetchClient.remoteSet(payload, false);

							if (!remoteSetResp.ok) {
								throw new BridgeClientError(remoteSetResp.statusText);
							}

							setModalState({ state: 'closed' });
							resolve();
						} catch (err) {
							reject(
								new BridgeClientError(`API call was rejected with the following error: ${err}`),
							);
						}
					},
					domainInfo: {
						type: 'REMOTE',
						payload,
						manifestRemoteMap,
					},
					appName: app.name,
					termsOfUseLink: app.termsOfService,
					privacyPolicyLink: app.privacyPolicy,
				});
			});
		},
	};
}
